// O plural do i18next usa Intl.PluralRules, que o Hermes pode não ter; o
// polyfill só se instala se estiver faltando (no Jest/node é no-op).
import 'intl-pluralrules';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { resources } from '@/locales';
import { useAppStore } from '@/stores/useAppStore';
import {
    PREFERENCES_STORAGE_KEY,
    usePreferencesStore,
    type CurrencyCode,
    type DateFormatOption,
    type LanguagePreference,
} from '@/stores/usePreferencesStore';

export type AppLanguage = 'pt-BR' | 'en';

/** pt-BR é fonte da verdade E fallback: chave sem EN mostra o texto PT, nunca a tag (ADR 0044). */
export const FALLBACK_LANGUAGE: AppLanguage = 'pt-BR';

// Init SÍNCRONO no import (recursos inline, sem backend): testes e o primeiro
// render sempre têm PT disponível; o idioma definitivo é aplicado pelo
// initAppLanguage() no boot, antes do 1º render com strings (isReady).
void i18n.use(initReactI18next).init({
    resources,
    lng: FALLBACK_LANGUAGE,
    fallbackLng: FALLBACK_LANGUAGE,
    defaultNS: 'common',
    initAsync: false,
    interpolation: { escapeValue: false }, // React já escapa
    returnNull: false,
    // Dispara só quando a chave falta em TODOS os idiomas (= tag crua na tela).
    // Lacuna apenas no EN não passa aqui (cai no fallback) — essa é auditada
    // pelo locales/__tests__/check-i18n.test.ts.
    saveMissing: __DEV__,
    missingKeyHandler: (_lngs, ns, key) => {
        if (__DEV__) console.warn(`[i18n] chave inexistente: ${ns}:${key}`);
    },
});

export function resolveDeviceLanguage(): AppLanguage {
    try {
        // Lazy: expo-localization é módulo nativo — num dev client antigo (sem
        // rebuild) o import não pode derrubar o boot; sem ele, fica no pt-BR.
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { getLocales } = require('expo-localization') as typeof import('expo-localization');
        const code = getLocales()[0]?.languageCode;
        if (!code) return FALLBACK_LANGUAGE;
        return code === 'pt' ? 'pt-BR' : 'en';
    } catch {
        return FALLBACK_LANGUAGE;
    }
}

export function resolveLanguage(preference: LanguagePreference): AppLanguage {
    return preference === 'system' ? resolveDeviceLanguage() : preference;
}

/**
 * Moeda + formato de data pela REGIÃO do dispositivo, SÓ para instalação nova (F4).
 * A moeda no Kontto é só símbolo de exibição — mudar o default não converte valores.
 * currencyCode do expo-localization → BRL/USD/EUR (fallback BRL); região US → MM/dd/yyyy.
 */
export function resolveDeviceRegionDefaults(): { currency: CurrencyCode; dateFormat: DateFormatOption } {
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { getLocales } = require('expo-localization') as typeof import('expo-localization');
        const locale = getLocales()[0];
        const currency: CurrencyCode =
            locale?.currencyCode === 'USD' ? 'USD' : locale?.currencyCode === 'EUR' ? 'EUR' : 'BRL';
        const dateFormat: DateFormatOption = locale?.regionCode === 'US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
        return { currency, dateFormat };
    } catch {
        return { currency: 'BRL', dateFormat: 'dd/MM/yyyy' };
    }
}

async function applyLanguage(language: AppLanguage): Promise<void> {
    if (i18n.language !== language) await i18n.changeLanguage(language);
}

// Troca ao vivo: o seletor nas Configurações só grava a preferência; quem aplica
// no i18next é este subscribe (evita o ciclo store → i18n → store).
usePreferencesStore.subscribe((state, prev) => {
    if (state.language !== prev.language) {
        void applyLanguage(resolveLanguage(state.language));
    }
});

/**
 * Decide e aplica o idioma no boot (chamar DEPOIS do useAppStore.hydrate()).
 *
 * Lê o JSON persistido cru em vez do estado do store porque, depois da
 * re-hidratação, "nunca decidiu" e "escolheu 'system'" são indistinguíveis — e é
 * essa diferença que define o destino de quem já usava o app antes do i18n:
 * instalação pré-i18n (onboarding concluído, sem `language` gravado) é cravada
 * em pt-BR — inclusive quem usa o celular em inglês; instalação nova segue o
 * sistema. A decisão é gravada UMA vez via setLanguage. Ver ADR 0044.
 */
export async function initAppLanguage(): Promise<void> {
    try {
        const raw = await AsyncStorage.getItem(PREFERENCES_STORAGE_KEY);
        const persisted = raw
            ? (JSON.parse(raw) as { state?: { language?: LanguagePreference } }).state?.language
            : undefined;

        const preference = persisted
            ?? (useAppStore.getState().onboardingCompleted ? 'pt-BR' : 'system');
        if (!persisted) usePreferencesStore.getState().setLanguage(preference);

        // Instalação NOVA (sem NENHUMA preferência persistida): moeda e formato de
        // data seguem a região do aparelho (F4). Instalação pré-i18n tem `raw` != null
        // e mantém a moeda que o usuário já usa — não sobrescrevemos.
        if (raw == null) {
            const { currency, dateFormat } = resolveDeviceRegionDefaults();
            const store = usePreferencesStore.getState();
            store.setCurrency(currency);
            store.setDateFormat(dateFormat);
        }

        await applyLanguage(resolveLanguage(preference));
    } catch {
        // Qualquer falha aqui não pode segurar o boot: fica no fallback pt-BR.
        await applyLanguage(FALLBACK_LANGUAGE).catch(() => {});
    }
}

export default i18n;
